/**
 * PageHero — shared dark gradient banner used at the top of every in-app
 * tool page (Dashboard, Search Indexer, Admin Settings, Onboarding), so
 * back-navigation, title/subtitle layout, and the brand gradient stay
 * identical everywhere instead of each page hand-rolling its own.
 */

import type { ReactNode } from 'react';
import { ChevronLeft20Regular } from '@fluentui/react-icons';

interface BreadcrumbCrumb {
  label: string;
  onClick?: () => void;
}

interface PageHeroProps {
  icon: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  eyebrow?: { icon?: ReactNode; label: string };
  /** First crumb is the clickable "back" link; the last is the current page (plain text). */
  breadcrumbs?: BreadcrumbCrumb[];
  /** Right-aligned slot for a badge, gauge, or action button. */
  rightSlot?: ReactNode;
  /** Rendered in the left column, directly under the subtitle (e.g. a site picker, a "last scan" timestamp). */
  belowSubtitle?: ReactNode;
  /** Rendered full-width beneath the whole title/rightSlot row (e.g. a stat card grid). */
  children?: ReactNode;
}

export default function PageHero({
  icon,
  title,
  subtitle,
  eyebrow,
  breadcrumbs,
  rightSlot,
  belowSubtitle,
  children,
}: PageHeroProps) {
  return (
    <div
      style={{
        background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 60%, #0e7490 100%)',
      }}
      className='px-6 sm:px-10 lg:px-16 py-8 border-b-2 border-[#2ba6bb]/40'
    >
      <div className='max-w-7xl mx-auto'>
        {breadcrumbs && breadcrumbs.length > 0 && (
          <div className='flex items-center gap-1.5 text-sm mb-4'>
            {breadcrumbs.map((crumb, i) => {
              const isLast = i === breadcrumbs.length - 1;
              return (
                <span key={crumb.label} className='flex items-center gap-1.5'>
                  {i > 0 && <span className='text-white/30'>›</span>}
                  {!crumb.onClick ? (
                    <span className={isLast ? 'text-white/60' : 'text-white/40'}>
                      {crumb.label}
                    </span>
                  ) : (
                    <button
                      type='button'
                      onClick={crumb.onClick}
                      className='flex items-center gap-1 text-white/60 hover:text-white transition-colors focus:outline-none'
                    >
                      {i === 0 && <ChevronLeft20Regular style={{ width: 16, height: 16 }} />}
                      {crumb.label}
                    </button>
                  )}
                </span>
              );
            })}
          </div>
        )}

        <div className='flex flex-col md:flex-row md:items-start justify-between gap-8'>
          <div className='flex-1 min-w-0'>
            {eyebrow && (
              <div className='inline-flex items-center gap-2 bg-[#2ba6bb]/20 border border-[#2ba6bb]/30 text-[#67e8f9] text-xs uppercase tracking-widest font-bold px-3 py-1.5 rounded-full mb-4'>
                {eyebrow.icon}
                {eyebrow.label}
              </div>
            )}
            <div className='flex items-center gap-3'>
              <span
                className='flex items-center justify-center rounded-xl bg-[#2ba6bb]/35 text-[#2ba6bb] shrink-0'
                style={{ width: 40, height: 40 }}
              >
                {icon}
              </span>
              <h1 className='text-2xl sm:text-[28px] font-extrabold text-white leading-tight'>
                {title}
              </h1>
            </div>
            {subtitle && (
              <p className='text-blue-200/70 mt-2 text-sm max-w-lg leading-relaxed'>{subtitle}</p>
            )}
            {belowSubtitle}
          </div>

          {rightSlot && <div className='flex flex-col items-center gap-4 shrink-0'>{rightSlot}</div>}
        </div>

        {children}
      </div>
    </div>
  );
}
